import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import Loading from "./Loading";
import { useState } from "react";
import moment from "moment";
import { classCadaster, classOptions } from "../services/class";
import { useMediaQuery, useTheme } from "@mui/material";

const daysOfWeek = [
  { id: "1", name: "Dom" },
  { id: "2", name: "Seg" },
  { id: "3", name: "Ter" },
  { id: "4", name: "Qua" },
  { id: "5", name: "Qui" },
  { id: "6", name: "Sex" },
  { id: "7", name: "Sab" },
];

const ClassRegister = ({
  open,
  onClose,
  refetch,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}) => {
  const [value, setValue] = useState("1");
  const [fightId, setFightId] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [professorId, setProfessorId] = useState<string[]>([]);
  const [dayWeek, setDayWeek] = useState<string[]>([]);
  // const [checked, setChecked] = useState(false);
  const [studentLimit] = useState(false);
  const [checkinLock, setCheckinLock] = useState(false);

  const handleDayWeek = (
    _event: React.MouseEvent<HTMLElement>,
    paymentsType: string[]
  ) => {
    setDayWeek(paymentsType);
  };

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    data: dataOptions,
    isLoading: isLoadingOptions,
    isFetching: isFetchingOptions,
  }: {
    data: {
      professor: { id: string; fullName: string }[];
      spaces: [{ id: string; name: string }];
      fights: [{ id: string; name: string }];
    };
    isLoading: boolean;
    isFetching: boolean;
  } = useQuery({
    queryFn: classOptions,
    queryKey: ["classOptions"],
    initialData: {
      professor: [],
      spaces: [],
      fights: [],
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: classCadaster,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Aula cadastrada");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { values, handleChange, handleSubmit } = useFormik({
    initialValues: {
      name: "",
      description: "",
      fightsId: "",
      spacesId: "",
      professorId: "",
      limit: "",
      dayWeek: [],
      automaticCompletion: Boolean(),
      startTime: moment().format("HH:mm").toString(),
      endTime: moment().add(1, "hour").format("HH:mm").toString(),
      recurrent: "1",
    },
    onSubmit: (values) => {
      mutate({
        ...values,
        startTime: moment(values.startTime, "HH:mm").toDate(),
        endTime: moment(values.endTime, "HH:mm").toDate(),
        fightsId: fightId,
        spacesId: spaceId,
        professorId,
        dayWeek: dayWeek.join(","),
        // automaticCompletion: checked,
        checkinLock,
      });
    },
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog maxWidth="xs" open={open} onClose={() => onClose()}>
      <Loading loading={isPending || isLoadingOptions || isFetchingOptions} />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Nova turma
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton onClick={() => onClose()}>
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              Informe os dados da turma que você quer criar.
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                marginBottom: 2,
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Dia e hora" value="1" />
              <Tab label="Dados" value="2" disabled={Number(value) < 2} />
              <Tab label="Professor" value="3" disabled={Number(value) < 3} />
            </Tabs>
          </Grid2>
          {value === "1" && (
            <Grid2 size={12}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Dias da semana
                    </Typography>
                    <ToggleButtonGroup
                      sx={{
                        marginTop: 1,
                        "& .MuiToggleButton-root": {
                          padding: isMobile ? "4px 6px" : "6px 12px",
                          fontSize: isMobile ? "10px" : "12px",
                        },
                      }}
                      id="dayWeek"
                      value={dayWeek}
                      onChange={handleDayWeek}
                    >
                      {daysOfWeek?.map((item) => (
                        <ToggleButton
                          key={item?.id}
                          value={item?.id}
                          selected={dayWeek?.includes(item?.id)}
                        >
                          <Typography sx={{ fontSize: 12 }}>
                            {item?.name}
                          </Typography>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Stack>
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="startTime"
                    name="startTime"
                    label="Horário início"
                    type="time"
                    fullWidth
                    size="small"
                    value={values.startTime}
                    onChange={handleChange}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.showPicker()
                    }
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="endTime"
                    name="endTime"
                    label="Horário fim"
                    type="time"
                    fullWidth
                    size="small"
                    value={values.endTime}
                    onChange={handleChange}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.showPicker()
                    }
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (dayWeek.length === 0) {
                        toast.error("Selecione pelo menos um dia da semana");
                        return;
                      }

                      setValue("2");
                    }}
                  >
                    Próximo
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          )}
          {value === "2" && (
            <Grid2 size={12}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Input
                    id="name"
                    name="name"
                    label="Nome"
                    placeholder="Nome da turma"
                    type="text"
                    fullWidth
                    size="small"
                    value={values.name}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="description"
                    name="description"
                    label="Descrição"
                    placeholder="Descrição"
                    type="text"
                    fullWidth
                    size="small"
                    value={values.description}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    Modalidades
                  </Typography>
                  <Select
                    sx={{ marginTop: 1 }}
                    id="fightId"
                    value={fightId}
                    fullWidth
                    onChange={handleChange}
                  >
                    {dataOptions?.fights?.map((item) => (
                      <MenuItem
                        key={item?.id}
                        value={item?.id}
                        onClick={() => setFightId(item?.id)}
                      >
                        {item?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>
                <Grid2 size={12}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    Espaços
                  </Typography>
                  <Select
                    sx={{ marginTop: 1 }}
                    id="spaceId"
                    value={spaceId}
                    fullWidth
                    onChange={handleChange}
                  >
                    {dataOptions?.spaces?.map((item) => (
                      <MenuItem
                        key={item?.id}
                        value={item?.id}
                        onClick={() => setSpaceId(item?.id)}
                      >
                        {item?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>
                {/* <Grid2 size={12}>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Limite de alunos?
                    </Typography>
                    <Switch
                      checked={studentLimit}
                      onClick={() => setStudentLimit(!studentLimit)}
                    />
                  </Stack>
                </Grid2> */}
                {studentLimit && (
                  <Grid2 size={12}>
                    <Input
                      id="limit"
                      name="limit"
                      label="Limite"
                      placeholder="Limite de alunos"
                      type="text"
                      fullWidth
                      size="small"
                      value={values.limit}
                      onChange={handleChange}
                    />
                  </Grid2>
                )}
                {/* <Grid2 size={12}>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Finalizar aula automaticamente?
                    </Typography>
                    <Switch
                      checked={checked}
                      onClick={() => setChecked(!checked)}
                    />
                  </Stack>
                </Grid2> */}
                <Grid2 size={12}>
                  <Divider />
                  <Stack
                    sx={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Trava de check-in?
                    </Typography>
                    <Switch
                      checked={checkinLock}
                      onClick={() => setCheckinLock(!checkinLock)}
                    />
                  </Stack>
                  <Typography
                    sx={{ fontSize: 12, color: "secondary.contrastText" }}
                  >
                    Se ativada, os alunos não conseguem fazer check-in depois de
                    10 minutos da aula iniciada.
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (!values.name) {
                        toast.error("Digite o nome da aula");
                        return;
                      }
                      if (!fightId) {
                        toast.error("Digite a modalidade");
                        return;
                      }
                      if (!spaceId) {
                        toast.error("Digite o espaço");
                        return;
                      }
                      setValue("3");
                    }}
                  >
                    Próximo
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          )}
          {value === "3" && (
            <Grid2 size={12}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    Selecione o professor
                  </Typography>
                </Grid2>
                {dataOptions?.professor?.length > 0 && (
                  <Grid2
                    width="100%"
                    container
                    spacing={2}
                    borderRadius={2}
                    bgcolor="secondary.main"
                  >
                    {dataOptions?.professor?.map((item) => (
                      <Grid2 key={item?.id} size={12}>
                        <Stack
                          sx={{
                            width: "100%",
                            flexDirection: "row",
                            backgroundColor: "secondary.dark",
                            alignItems: "center",
                            gap: 1,
                            padding: 1,
                            borderRadius: 2,
                          }}
                        >
                          <Checkbox
                            value={item?.id}
                            checked={professorId.includes(item?.id)}
                            onClick={() =>
                              professorId.includes(item?.id)
                                ? setProfessorId([])
                                : setProfessorId([item?.id])
                            }
                          />
                          {/* <Avatar
                        alt="student_avatar"
                        src="https://picsum.photos/200"
                        sx={{
                          width: 30,
                          height: 30,
                        }}
                      /> */}
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            fontSize={16}
                          >
                            {item?.fullName}
                          </Typography>
                        </Stack>
                      </Grid2>
                    ))}
                  </Grid2>
                )}
                {dataOptions?.professor?.length === 0 && (
                  <Grid2 size={12}>
                    <Typography
                      variant="body1"
                      fontSize={16}
                      fontWeight={600}
                      sx={{
                        padding: 1,
                        borderRadius: 2,
                        backgroundColor: "secondary.dark",
                        textAlign: "center",
                      }}
                    >
                      Nenhum professor cadastrado
                    </Typography>
                  </Grid2>
                )}
                <Grid2 size={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Criar aula
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          )}
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default ClassRegister;
